export type WordPath = {
  word: string;
  cells: [number, number][];
};

const DIRECTIONS: [number, number][] = [
  [0, 1],
  [0, -1],
  [1, 0],
  [-1, 0],
];

export default function findWordPaths(
  table: string[][],
  words: string[],
  type: 1 | 2 ,
): WordPath[] {
  const result: WordPath[] = [];

  const occupied = new Set<string>();

  for (const word of words) {
    const cells = findPath(table, word, type, occupied);

    if (!cells) {
      if (type === 2) {
        throw new Error(
          `Слово ${word} не найдено или не может начинаться с края таблицы без пересечения с другими словами`,
        );
      }

      throw new Error(`Слово ${word} не найдено в таблице`);
    }

    result.push({
      word,
      cells,
    });

    if (type === 2) {
      for (const [row, col] of cells) {
        occupied.add(`${row},${col}`);
      }
    }
  }

  return result;
}

function findPath(
  table: string[][],
  word: string,
  type: 1 | 2,
  occupied: Set<string>,
): [number, number][] | null {
  const rows = table.length;
  const columns = table[0].length;

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < columns; col++) {
      if (table[row][col] !== word[0]) {
        continue;
      }

      if (type === 2 && !isEdgeCell(row, col, rows, columns)) {
        continue;
      }
      
      if (type === 2 && occupied.has(`${row},${col}`)) {
        continue;
      }

      const path: [number, number][] = [[row, col]];

      const used = new Set<string>();
      used.add(`${row},${col}`);

      const result = search(
        table,
        word,
        row,
        col,
        1,
        path,
        used,
      );

      if (result) {
        return result;
      }
    }
  }

  return null;
}

function search(
  table: string[][],
  word: string,
  row: number,
  col: number,
  index: number,
  path: [number, number][],
  used: Set<string>,
): [number, number][] | null {
  if (index === word.length) {
    return [...path];
  }

  for (const [dr, dc] of DIRECTIONS) {
    const nextRow = row + dr;
    const nextCol = col + dc;

    if (
      nextRow < 0 ||
      nextRow >= table.length ||
      nextCol < 0 ||
      nextCol >= table[0].length
    ) {
      continue;
    }

    if (table[nextRow][nextCol] !== word[index]) {
      continue;
    }

    const key = `${nextRow},${nextCol}`;

    if (used.has(key)) {
      continue;
    }

    used.add(key);
    path.push([nextRow, nextCol]);

    const result = search(
      table,
      word,
      nextRow,
      nextCol,
      index + 1,
      path,
      used,
    );

    if (result) {
      return result;
    }

    path.pop();
    used.delete(key);
  }

  return null;
}

function isEdgeCell(
  row: number,
  col: number,
  rows: number,
  columns: number,
): boolean {
  return (
    row === 0 ||
    row === rows - 1 ||
    col === 0 ||
    col === columns - 1
  );
}